import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Check,
  ArrowLeft,
  ArrowRight,
  Target,
  TrendingUp,
  Rocket,
  Calendar,
  Clock,
  Users,
  Star,
  Wallet,
  MessageCircle,
  ChevronRight,
  Sparkles,
  FileText
} from "lucide-react";
import { trackCTAClick } from "@/utils/analytics";

// Package data configuration
const PACKAGES = {
  "strategic-foundation": {
    id: "strategic_foundation",
    name: "Strategic Foundation Retainer",
    tagline: "Build Your Business on Solid Ground",
    price: "$750",
    period: "/month",
    commitment: "3-Month Minimum",
    icon: Target,
    color: "blue",
    paymentPlanUrl: "https://request.financemutual.com/pay_overtime?product_code=pc_8e2a028bc1a9cd91396e2b600b6c7b68",
    heroDescription: "For entrepreneurs who need structure, clarity, and consistent strategic guidance to build a strong foundation for sustainable growth.",
    overview: `The Strategic Foundation Retainer is designed for entrepreneurs who are generating revenue but need help bringing clarity, structure, and consistency to their business operations. This is your first step toward building a business that runs smoothly and scales sustainably.

Whether you're feeling scattered, overwhelmed by decisions, or unsure where to focus next, this retainer provides the strategic guidance and accountability you need to move forward with confidence.`,
    whatsIncluded: [
      {
        title: "Monthly 90-Minute Strategic Clarity Session",
        description: "Deep-dive strategy sessions to review progress, solve challenges, and plan your next moves"
      },
      {
        title: "Consultant-Led Priority Planning",
        description: "Expert guidance on what to focus on and what to deprioritize for maximum impact"
      },
      {
        title: "Business Overview & KPI Dashboard",
        description: "Access to your personalized dashboard to track key metrics and progress"
      },
      {
        title: "AI-Powered Planning Tools",
        description: "Leverage our intelligent tools to streamline decision-making and planning"
      },
      {
        title: "Email Support (48-Hour Response)",
        description: "Direct access to your consultant for questions and guidance between sessions"
      },
      {
        title: "Resource Library Access",
        description: "Templates, guides, and frameworks to support your business operations"
      }
    ],
    bestFor: [
      "Entrepreneurs generating revenue but feeling scattered or stuck",
      "Business owners who need structure and consistent guidance",
      "Founders building their foundation before scaling",
      "Those who prefer occasional, focused guidance over intensive support"
    ],
    outcomes: [
      "Clear priorities and focused action plans",
      "Reduced overwhelm and decision fatigue",
      "Consistent progress tracking",
      "Foundation for sustainable growth"
    ],
    faqs: [
      {
        q: "How do the monthly sessions work?",
        a: "We schedule a 90-minute video call each month where we review your progress, address challenges, and plan your priorities for the coming weeks. Sessions are recorded for your reference."
      },
      {
        q: "What if I need help between sessions?",
        a: "You have email support with a 48-hour response time. For urgent matters, we can arrange brief check-in calls."
      },
      {
        q: "Why is there a 3-month minimum?",
        a: "Strategic change takes time to implement and measure. The 3-month commitment ensures we have enough runway to create meaningful, lasting results."
      }
    ]
  },
  "growth-optimization": {
    id: "growth_optimization",
    name: "Growth & Optimization Retainer",
    tagline: "Turn Strategy Into Measurable Momentum",
    price: "$1,500",
    period: "/month",
    commitment: "3–6 Month Engagement",
    popular: true,
    icon: TrendingUp,
    color: "brand",
    paymentPlanUrl: "https://request.financemutual.com/pay_overtime?product_code=pc_04783700022f2507cad16946aa4c8002",
    heroDescription: "For established entrepreneurs ready to optimize systems, refine their offers, and scale revenue with deeper execution support and accountability.",
    overview: `The Growth & Optimization Retainer is built for business owners who have achieved consistent revenue and are ready to level up. You've laid the foundation—now it's time to optimize your systems, sharpen your positioning, and accelerate growth.

This engagement provides more frequent touchpoints, advanced analytics, and hands-on implementation support to help you move faster and smarter.`,
    whatsIncluded: [
      {
        title: "Bi-Weekly Strategy & Execution Sessions",
        description: "Twice-monthly 60-minute sessions for strategy refinement and execution planning"
      },
      {
        title: "Priority Consultant Access",
        description: "Faster response times and priority scheduling for your needs"
      },
      {
        title: "Advanced Analytics & Performance Tracking",
        description: "Deep insights into your business performance with actionable recommendations"
      },
      {
        title: "Marketing & Offer Optimization",
        description: "Expert guidance on improving your offers, pricing, and marketing effectiveness"
      },
      {
        title: "Competitor & Positioning Analysis",
        description: "Strategic analysis to help you stand out in your market"
      },
      {
        title: "AI-Assisted Implementation Tools",
        description: "Leverage intelligent automation to execute faster"
      },
      {
        title: "Accountability Tracking",
        description: "Structured follow-ups to keep you on track and moving forward"
      }
    ],
    bestFor: [
      "Established entrepreneurs with consistent revenue",
      "Business owners ready to optimize and scale",
      "Those who need regular accountability and execution support",
      "Founders looking to refine their positioning and offers"
    ],
    outcomes: [
      "Optimized business systems and processes",
      "Improved marketing effectiveness and conversion",
      "Clearer market positioning",
      "Accelerated revenue growth"
    ],
    faqs: [
      {
        q: "How is this different from the Strategic Foundation?",
        a: "This tier provides twice as many touchpoints (bi-weekly vs monthly), priority access, and more advanced analytics. It's designed for businesses that are beyond the foundation stage and ready for optimization."
      },
      {
        q: "What kind of results can I expect?",
        a: "Results vary, but clients typically see improvements in operational efficiency, marketing ROI, and revenue growth within the first 90 days."
      },
      {
        q: "Can I upgrade from Strategic Foundation?",
        a: "Absolutely! Many clients start with Strategic Foundation and upgrade to Growth & Optimization once they're ready for more intensive support."
      }
    ]
  },
  "scale-leadership": {
    id: "scale_leadership",
    name: "Scale & Leadership Retainer",
    tagline: "Strategic Partnership for Scaling Leaders",
    price: "$3,000–$5,000",
    period: "/month",
    commitment: "Application Only",
    exclusive: true,
    icon: Rocket,
    color: "amber",
    paymentPlanUrl: "https://request.financemutual.com/pay_overtime?product_code=pc_ad1046e6cd6e44091690a71a4da88946",
    heroDescription: "A high-touch strategic partnership for founders leading teams, managing complexity, and preparing to scale intentionally.",
    overview: `The Scale & Leadership Retainer is our most comprehensive engagement, designed for business leaders who are managing significant complexity—whether that's a growing team, multiple offers, or expanding operations.

This isn't coaching. It's a strategic partnership where we work alongside you as a trusted advisor, helping you navigate high-stakes decisions and scale with intention.

Due to the depth of involvement required, this engagement is by application only. We work with a limited number of Scale clients to ensure we can deliver exceptional results.`,
    whatsIncluded: [
      {
        title: "Weekly Strategic Advisory Sessions",
        description: "60-minute weekly sessions for high-level strategy and decision support"
      },
      {
        title: "Full Business Operating System Dashboard",
        description: "Comprehensive visibility into all aspects of your business performance"
      },
      {
        title: "Custom AI Workflows & SOPs",
        description: "Tailored automation and documentation for your unique operations"
      },
      {
        title: "Team Planning & Leadership Support",
        description: "Guidance on building, managing, and developing your team"
      },
      {
        title: "Quarterly Strategic Reviews",
        description: "In-depth quarterly analysis with forward-looking planning"
      },
      {
        title: "High-Level Decision Support",
        description: "On-call strategic guidance for major business decisions"
      }
    ],
    bestFor: [
      "Founders leading teams of 3+ people",
      "Businesses with multiple revenue streams or offers",
      "Leaders facing complex operational challenges",
      "Those preparing for significant growth or transitions"
    ],
    outcomes: [
      "Clear scalable systems and processes",
      "Stronger team leadership and delegation",
      "Strategic clarity on growth decisions",
      "Sustainable scaling framework"
    ],
    faqs: [
      {
        q: "Why is this application only?",
        a: "We limit Scale & Leadership engagements to ensure we can provide the depth of attention each client deserves. The application process helps us ensure we're a good fit for each other."
      },
      {
        q: "What does the application process involve?",
        a: "After you apply, we'll schedule a discovery call to learn about your business, challenges, and goals. If we're a good fit, we'll create a custom proposal."
      },
      {
        q: "What's the typical engagement length?",
        a: "Most Scale clients work with us for 6-12 months, though some choose to continue longer as an ongoing strategic partnership."
      }
    ]
  }
};

export const PackageDetail = () => {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const pkg = PACKAGES[packageId];

  if (!pkg) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-neutral-900 mb-4">Package Not Found</h1>
          <Button onClick={() => navigate("/pricing")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pricing
          </Button>
        </div>
      </div>
    );
  }

  const Icon = pkg.icon;
  const colorMap = {
    blue: { 
      bg: 'bg-blue-50', 
      text: 'text-blue-600', 
      border: 'border-blue-200',
      badge: 'bg-blue-100 text-blue-700',
      gradient: 'from-blue-50 to-white',
      ring: 'ring-blue-500'
    },
    brand: { 
      bg: 'bg-[#39FF14]/10', 
      text: 'text-[#39FF14]', 
      border: 'border-[#39FF14]/30',
      badge: 'bg-[#39FF14]/20 text-[#2BC40E]',
      gradient: 'from-emerald-50 to-white',
      ring: 'ring-[#39FF14]'
    },
    amber: { 
      bg: 'bg-amber-50', 
      text: 'text-amber-600', 
      border: 'border-amber-200',
      badge: 'bg-amber-100 text-amber-700',
      gradient: 'from-amber-50 to-white',
      ring: 'ring-amber-500'
    }
  };
  const colors = colorMap[pkg.color];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/mybiz-logo.png" 
                alt="My Biz Consulting" 
                className="h-10 w-auto"
              />
            </Link>
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/pricing")}
                className="text-sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                All Packages
              </Button>
              <Button 
                onClick={() => navigate("/clarity-assessment")}
                className="bg-neutral-900 hover:bg-neutral-800 text-white text-sm"
              >
                Free Assessment
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={`pt-28 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b ${colors.gradient}`}>
        <div className="max-w-4xl mx-auto">
          <Link to="/pricing" className="inline-flex items-center text-neutral-500 hover:text-neutral-700 mb-6">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to All Packages
          </Link>
          
          <div className="flex items-start gap-6 mb-6">
            <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-8 h-8 ${colors.text}`} />
            </div>
            <div>
              {pkg.popular && (
                <Badge className="bg-[#39FF14] text-black mb-2">
                  <Star className="w-3 h-3 mr-1" />
                  Most Popular
                </Badge>
              )}
              {pkg.exclusive && (
                <Badge className={colors.badge + " mb-2"}>Application Only</Badge>
              )}
              <h1 className="text-3xl sm:text-4xl font-semibold text-neutral-900 mb-2">
                {pkg.name}
              </h1>
              <p className="text-xl text-neutral-600">{pkg.tagline}</p>
            </div>
          </div>

          <p className="text-lg text-neutral-600 mb-8">
            {pkg.heroDescription}
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-8">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-neutral-900">{pkg.price}</span>
              <span className="text-neutral-500 text-lg">{pkg.period}</span>
            </div>
            <Badge variant="outline" className="text-base px-4 py-1">
              <Clock className="w-4 h-4 mr-1" />
              {pkg.commitment}
            </Badge>
          </div>

          <div className="flex flex-wrap gap-4">
            <Button 
              size="lg"
              onClick={() => {
                trackCTAClick(`Get Started - ${pkg.name}`, 'package_detail');
                if (pkg.exclusive) {
                  navigate('/apply/scale');
                } else {
                  navigate('/register');
                }
              }}
              className={`${
                pkg.popular 
                  ? 'bg-[#39FF14] hover:bg-[#32E010] text-black' 
                  : 'bg-neutral-900 hover:bg-neutral-800 text-white'
              }`}
              data-testid="package-cta-primary"
            >
              {pkg.exclusive ? "Apply Now" : "Get Started"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            {pkg.paymentPlanUrl && (
              <a 
                href={pkg.paymentPlanUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCTAClick('Payment Plan', pkg.id)}
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-dashed border-green-300 rounded-lg text-green-700 font-medium hover:bg-green-50 hover:border-green-400 transition-colors"
                data-testid="package-payment-plan"
              >
                <Wallet className="w-5 h-5" />
                Payment Plan Available
              </a>
            )}
            
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/clarity-assessment')}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Take Free Assessment First
            </Button>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-6">Overview</h2>
          <div className="prose prose-neutral max-w-none">
            {pkg.overview.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="text-neutral-600 text-lg leading-relaxed mb-4">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className={`py-16 px-4 sm:px-6 lg:px-8 ${colors.bg}`}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-8">What's Included</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {pkg.whatsIncluded.map((item, idx) => (
              <Card key={idx} className="bg-white border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-8 h-8 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Check className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-1">{item.title}</h3>
                      <p className="text-neutral-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Best For Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-semibold text-neutral-900 mb-6">
                <Users className={`w-6 h-6 inline mr-2 ${colors.text}`} />
                Best For
              </h2>
              <ul className="space-y-4">
                {pkg.bestFor.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
                    <span className="text-neutral-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-neutral-900 mb-6">
                <Target className={`w-6 h-6 inline mr-2 ${colors.text}`} />
                Expected Outcomes
              </h2>
              <ul className="space-y-4">
                {pkg.outcomes.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Star className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
                    <span className="text-neutral-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-neutral-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {pkg.faqs.map((faq, idx) => (
              <Card key={idx} className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-neutral-900 mb-2">{faq.q}</h3>
                  <p className="text-neutral-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-neutral-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-semibold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-neutral-400 mb-8 max-w-2xl mx-auto">
            {pkg.exclusive 
              ? "Apply now and let's discuss how we can support your scaling journey."
              : "Start with a free Business Clarity Assessment or enroll directly."}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => {
                trackCTAClick(`Bottom CTA - ${pkg.name}`, 'package_detail');
                if (pkg.exclusive) {
                  navigate('/apply/scale');
                } else {
                  navigate('/register');
                }
              }}
              className="bg-[#39FF14] hover:bg-[#32E010] text-black"
            >
              {pkg.exclusive ? "Submit Application" : "Enroll Now"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/clarity-assessment')}
              className="border-neutral-600 text-white hover:bg-neutral-800"
            >
              Take Free Assessment
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/request-project')}
              className="border-neutral-600 text-white hover:bg-neutral-800"
            >
              <FileText className="w-5 h-5 mr-2" />
              Request Custom Scope
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <img 
                src="/mybiz-logo.png" 
                alt="My Biz Consulting" 
                className="h-8 w-auto brightness-0 invert"
              />
            </div>
            <div className="flex items-center space-x-6 text-sm text-neutral-500">
              <Link to="/pricing" className="hover:text-neutral-300">All Packages</Link>
              <Link to="/clarity-assessment" className="hover:text-neutral-300">Free Assessment</Link>
              <Link to="/login" className="hover:text-neutral-300">Login</Link>
            </div>
            <p className="text-neutral-600 text-sm">
              © {new Date().getFullYear()} My Biz Consulting
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PackageDetail;
